import { useRef, useState } from 'react';
import { FieldError, TextField } from 'react-aria-components';
import { Control, FieldValues, Path, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { cn } from '~/lib/utils';
import Icon from '../ui/Icon';

const MAX_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = 'image/x-png, image/png, image/jpeg, image/jpg';

type FileFieldChildrenArgs = {
  files: File[];
  fileName: string | null;
  onSelect: () => void;
  onRemove: (index?: number) => void;
  canAddMore: boolean;
  isDragOver: boolean;
};

type FileFieldProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  children: (args: FileFieldChildrenArgs) => React.ReactNode;
  className?: string;
  multiple?: boolean;
  maxFiles?: number;
};

const coerceToArray = (val: unknown): File[] => {
  if (!val) return [];
  if (Array.isArray(val)) return val as File[];
  // Support backward-compat single-file values
  if (val instanceof File) return [val];
  return [];
};

function FileField<T extends FieldValues>({
  control,
  name,
  children,
  className,
  multiple = false,
  maxFiles = 5,
}: FileFieldProps<T>) {
  const { t } = useTranslation('', { keyPrefix: 'common.form.fileUpload' });
  const inputRef = useRef<HTMLInputElement>(null);
  const [, setDroppedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragDepth = useRef(0);

  const {
    field,
    fieldState: { error, invalid },
  } = useController({
    name,
    control,
  });

  const validateFiles = (files: File[]): boolean => {
    for (const f of files) {
      if (f.size > MAX_SIZE) {
        control.setError(name, {
          message: t('maxSizeError', { size: MAX_SIZE / 1024 / 1024 }),
        });
        return false;
      }
    }
    return true;
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event?.target?.files ?? []);

    if (inputRef.current) {
      inputRef.current.value = '';
    }

    if (!selected.length || !validateFiles(selected)) {
      return;
    }

    if (multiple) {
      const current = coerceToArray(field.value);
      const next = [...current, ...selected].slice(0, maxFiles);
      field.onChange(next);
    } else {
      field.onChange(selected[0]);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();

    dragDepth.current = 0;
    setIsDragOver(false);

    const dropped = Array.from(event.dataTransfer?.files ?? []);
    if (!dropped.length || !validateFiles(dropped)) {
      return;
    }

    if (multiple) {
      const current = coerceToArray(field.value);
      const next = [...current, ...dropped].slice(0, maxFiles);
      setDroppedFile(dropped[0] ?? null);
      field.onChange(next);
    } else {
      const file = dropped[0];
      setDroppedFile(file ?? null);
      field.onChange(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();

    dragDepth.current += 1;
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();

    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setIsDragOver(false);
    }
  };

  const files = coerceToArray(field.value);
  const canAddMore = multiple
    ? typeof maxFiles === 'number'
      ? files.length < maxFiles
      : true
    : !field.value;

  return (
    <TextField isInvalid={invalid} aria-label={t('uploadFile')}>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        className={cn('w-full', className)}
      >
        <input
          type="file"
          className="hidden"
          onChange={handleChange}
          accept={ALLOWED_TYPES}
          ref={inputRef}
          multiple={multiple}
        />
        {children({
          files,
          fileName: files[0]?.name ?? (field.value as any)?.name ?? null,
          onSelect: () => inputRef.current?.click(),
          onRemove: (index?: number) => {
            if (multiple) {
              if (typeof index === 'number') {
                field.onChange(files.filter((_, i) => i !== index));
              } else {
                field.onChange([]);
              }
            } else {
              field.onChange('');
            }
            if (inputRef.current) {
              inputRef.current.value = '';
            }
          },
          canAddMore,
          isDragOver,
        })}
      </div>
      <FieldError className="mt-2 text-[13px] leading-[1.7] pl-6 flex gap-2 items-center">
        <Icon name="error" /> {error?.message}
      </FieldError>
    </TextField>
  );
}

export default FileField;
